import { useVirtualizer } from "@tanstack/react-virtual";
import { useEffect, useRef } from "react";
import type { RefObject } from "react";

const OVERSCAN = 8;
const UNMEASURED_WINDOW = 40;

/**
 * Vertical window over a tab scroller. The next page is requested only after
 * the list has been measured and the last mounted row is the loaded end —
 * a short unmeasured list must not drain every page.
 */
export function useHubVirtualList({
  count,
  estimateSize,
  scrollRef,
  hasMore = false,
  isFetchingMore = false,
  onLoadMore,
}: {
  count: number;
  estimateSize: number;
  scrollRef: RefObject<HTMLElement | null>;
  hasMore?: boolean;
  isFetchingMore?: boolean;
  onLoadMore?: () => void;
}) {
  const extra = hasMore || isFetchingMore ? 1 : 0;
  const virtualizer = useVirtualizer({
    count: count + extra,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => estimateSize,
    overscan: OVERSCAN,
  });
  const virtualItems = virtualizer.getVirtualItems();
  const measured = virtualItems.length > 0;
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;

  const lastIndex = measured ? virtualItems[virtualItems.length - 1]?.index : undefined;

  useEffect(() => {
    if (!measured || !hasMore || isFetchingMore || lastIndex == null) return;
    if (lastIndex >= count - 1) onLoadMoreRef.current?.();
  }, [measured, hasMore, isFetchingMore, lastIndex, count]);

  const indexes = measured
    ? virtualItems.map((item) => item.index)
    : Array.from({ length: Math.min(count + extra, UNMEASURED_WINDOW) }, (_, index) => index);
  const topPad = measured ? (virtualItems[0]?.start ?? 0) : 0;
  const bottomPad =
    measured && virtualItems.length > 0
      ? virtualizer.getTotalSize() - virtualItems[virtualItems.length - 1]!.end
      : 0;

  return { indexes, topPad, bottomPad };
}
